/**
 * Motion tokens shared by the host script and the chat iframe.
 *
 * Mirrors `src/styles/_motion.scss` (the CSS custom properties
 * `--dhsv-motion-*`). A Jest test compares the two, so change both together.
 * JS timeouts that trail a CSS animation are derived from these constants plus
 * `MOTION_SAFETY_MS`, never typed as literals.
 */

/** Surface enter: overlay fade-in, drawer, island grow, fullscreen enter. */
export const MOTION_OPEN_MS = 240;
/** Surface exit. */
export const MOTION_CLOSE_MS = 180;
/** Hover / press. */
export const MOTION_MICRO_MS = 120;
/** Reduced motion: opacity only, never longer than this (`$dhsv-motion-reduced-ms`). */
export const MOTION_REDUCED_MS = 100;
/**
 * Margin added to every timeout that waits for a CSS animation: roughly two
 * frames for rAF and style-flush jitter.
 */
export const MOTION_SAFETY_MS = 40;
/**
 * Lost-message timeout for the `expanded` handshake. Not an animation
 * duration, so it is a fixed constant rather than derived.
 */
export const MOTION_EXPANDED_FALLBACK_MS = 200;

/** The single curve, in the array form `motion/react` expects. */
export const MOTION_EASE = [ 0.32, 0.72, 0, 1 ] as const;
/** The same curve for CSS and inline styles. */
export const MOTION_EASE_CSS = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * Edge sweep ("Lichtkante", 03-animationen.md section 6.4): a light runs once
 * around the border of the growing morph surface. Duration in ms (scaled by
 * `?slow`) and its own curve, in both array and CSS form.
 */
export const MOTION_EDGE_SWEEP_MS = 640;
export const MOTION_EDGE_SWEEP_EASE = [ 0.35, 0.1, 0.25, 1 ] as const;
export const MOTION_EDGE_SWEEP_EASE_CSS = 'cubic-bezier(0.35, 0.1, 0.25, 1)';

/**
 * Spring tokens (iOS-style: `response` is the undamped period in seconds,
 * `damping` is zeta). `spring.ts` turns them into stiffness/damping and
 * `linear()` curves. Values from 03-animationen.md section 2.2 and 3.
 */
export const SPRING_LARGE_RESPONSE = 0.45;
export const SPRING_LARGE_DAMPING = 0.82;
export const SPRING_NAV_RESPONSE = 0.4;
export const SPRING_NAV_DAMPING = 0.82;
export const SPRING_SNAP_OPEN_W_RESPONSE = 0.4;
export const SPRING_SNAP_OPEN_W_DAMPING = 0.84;
export const SPRING_SNAP_OPEN_H_RESPONSE = 0.54;
export const SPRING_SNAP_OPEN_H_DAMPING = 0.86;
export const SPRING_SNAP_CLOSE_W_RESPONSE = 0.46;
export const SPRING_SNAP_CLOSE_W_DAMPING = 0.86;
export const SPRING_SNAP_CLOSE_H_RESPONSE = 0.38;
export const SPRING_SNAP_CLOSE_H_DAMPING = 0.84;

/**
 * CSS spring curves: the spring plus the duration the generated `linear()`
 * easing spans. `l` is the large-surface spring (same as SPRING_LARGE_*), `s`
 * small elements (press release, hover lift), `pop` the speech bubble pop.
 */
export const SPRING_CURVES = {
	l: {
		response: SPRING_LARGE_RESPONSE,
		damping: SPRING_LARGE_DAMPING,
		durationS: 0.62,
	},
	s: { response: 0.32, damping: 0.8, durationS: 0.46 },
	pop: { response: 0.42, damping: 0.68, durationS: 0.7 },
} as const;

/** Where the iframe drawer anchors a position-less open. */
export type TransformOriginRect = Pick<
	DOMRectReadOnly,
	'left' | 'top' | 'width' | 'height'
>;

export interface MotionViewport {
	width: number;
	height: number;
}

function isFinitePoint( value: unknown ): value is { x: number; y: number } {
	if ( ! value || typeof value !== 'object' ) {
		return false;
	}
	const point = value as Record< string, unknown >;
	return Number.isFinite( point.x ) && Number.isFinite( point.y );
}

function clamp( value: number, min: number, max: number ): number {
	return Math.min( Math.max( value, min ), max );
}

/**
 * Centre of an element's rect, clamped into the viewport. Returns `undefined`
 * for a missing, zero-size or non-finite rect so callers omit the field.
 * @param rect
 * @param viewport
 */
export function getRectCenter(
	rect: TransformOriginRect | null | undefined,
	viewport: MotionViewport
): { x: number; y: number } | undefined {
	if ( ! rect ) {
		return undefined;
	}
	const { left, top, width, height } = rect;
	if (
		! [ left, top, width, height ].every( Number.isFinite ) ||
		width <= 0 ||
		height <= 0
	) {
		return undefined;
	}
	return {
		x: clamp( left + width / 2, 0, viewport.width ),
		y: clamp( top + height / 2, 0, viewport.height ),
	};
}

/**
 * CSS `transform-origin` for an animated element from a viewport-coordinate
 * origin: the point is clamped into the viewport and expressed relative to the
 * element's own rect. The expanded iframe is `inset: 0`, so viewport and
 * iframe coordinates are the same space.
 *
 * A malformed origin (not an object, non-finite `x`/`y`) or a missing rect
 * resolves to `fallback` silently.
 * @param origin
 * @param rect
 * @param viewport
 * @param fallback
 */
export function resolveTransformOrigin(
	origin: unknown,
	rect: TransformOriginRect | null | undefined,
	viewport: MotionViewport,
	fallback?: string
): string | undefined {
	if ( ! isFinitePoint( origin ) || ! rect ) {
		return fallback;
	}
	if ( ! [ rect.left, rect.top ].every( Number.isFinite ) ) {
		return fallback;
	}
	const x = clamp( origin.x, 0, viewport.width ) - rect.left;
	const y = clamp( origin.y, 0, viewport.height ) - rect.top;
	return `${ Math.round( x ) }px ${ Math.round( y ) }px`;
}
